import React, { useState, useEffect } from 'react';
import CoordPickerModal from './CoordPickerModal';

// ─── Taxonomy ─────────────────────────────────────────────────────────────────
const CATEGORIES = {
  plants_medicine: 'Plants & Medicine',
  hunting:         'Hunting',
  birds:           'Birds',
  fishing:         'Fishing',
  environmental:   'Environmental',
  other:           'Other',
};

const SUBCATEGORIES = {
  plants_medicine: ['Foraging', 'Berry Picking', 'Root Digging', 'Bark Harvest', 'Seed Collection', 'Other'],
  hunting:         ['Rifle', 'Bow', 'Snare / Trap', 'Tree Stand', 'Ground Blind', 'Spot & Stalk', 'Other'],
  birds:           ['Shotgun', 'Decoy Hunt', 'Blind', 'Jump Shooting', 'Net', 'Other'],
  fishing:         ['From Shore', 'From Boat', 'Ice Fishing', 'Fly Fishing', 'Trolling', 'Net / Trap', 'Other'],
  environmental:   ['Pollution', 'Oil spill', 'Dead/dry wood', 'Water quality', 'Habitat damage', 'Other'],
  other:           [],
};

// Categories where subcategory means method/context rather than sub-type
const METHOD_CATEGORIES = new Set(['hunting', 'birds', 'fishing', 'plants_medicine']);

const SEASONS = [
  { value: 'spring', label: 'Spring' },
  { value: 'summer', label: 'Summer' },
  { value: 'fall',   label: 'Fall'   },
  { value: 'winter', label: 'Winter' },
];

// Derive season from a date string (YYYY-MM-DD). Northern-hemisphere meteorological seasons.
function seasonFromDate(dateStr) {
  if (!dateStr) return '';
  const month = parseInt(dateStr.slice(5, 7), 10);
  if (month >= 3 && month <= 5)  return 'spring';
  if (month >= 6 && month <= 8)  return 'summer';
  if (month >= 9 && month <= 11) return 'fall';
  return 'winter'; // Dec, Jan, Feb
}

const ABUNDANCE_OPTIONS = [
  { value: 'sparse',       label: 'Sparse' },
  { value: 'moderate',     label: 'Moderate' },
  { value: 'abundant',     label: 'Abundant' },
  { value: 'very_abundant',label: 'Very Abundant' },
];

// ─── Field style helpers ──────────────────────────────────────────────────────
const inputStyle = {
  display: 'block', width: '100%', padding: '8px 10px',
  border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block', fontWeight: 600, marginBottom: 4, fontSize: 14, color: '#374151',
};

const fieldStyle = { marginBottom: 16 };

function Field({ label, required, children }) {
  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
function emptyForm() {
  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD in local time
  return {
    category:            '',
    subcategory:         '',
    species:             '',
    latitude:            '',
    longitude:           '',
    locationDescription: '',
    harvestDate:         today,
    season:              seasonFromDate(today),
    quantity:            '',
    unit:                '',
    abundance:           '',
    observations:        '',
    traditionalKnowledge:'',
    isTraditionalUse:    false,
  };
}

/**
 * HarvestForm
 * Props:
 *   onSubmit(data)   — called with form payload
 *   onCancel()       — optional cancel handler
 *   loading          — disables submit while saving
 *   error            — string error message to show
 *   initial          — pre-filled values for edit mode
 */
export default function HarvestForm({ onSubmit, onCancel, loading, error, initial }) {
  const [form, setForm] = useState({ ...emptyForm(), ...initial });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState('');

  // Re-seed form when intent changes (initial gets new category pre-fill)
  useEffect(() => {
    setForm({ ...emptyForm(), ...(initial || {}) });
  }, [JSON.stringify(initial)]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleUseMyLocation() {
    if (!navigator.geolocation) {
      setGpsError('Geolocation is not supported by your browser.');
      return;
    }
    setGpsLoading(true);
    setGpsError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((prev) => ({
          ...prev,
          latitude: String(pos.coords.latitude),
          longitude: String(pos.coords.longitude),
        }));
        setGpsLoading(false);
      },
      () => {
        setGpsError('Could not get your location. Try picking on the map instead.');
        setGpsLoading(false);
      },
      { timeout: 10000 }
    );
  }

  function set(field) {
    return (e) => {
      const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => {
        const next = { ...prev, [field]: val };
        // Reset subcategory when category changes
        if (field === 'category') next.subcategory = '';
        // Auto-derive season from harvest date
        if (field === 'harvestDate') next.season = seasonFromDate(val);
        return next;
      });
    };
  }

  function handlePickerConfirm(lat, lng) {
    setForm((prev) => ({ ...prev, latitude: lat, longitude: lng }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    onSubmit({ ...form });
  }

  const subs = SUBCATEGORIES[form.category] || [];
  const subcatLabel = METHOD_CATEGORIES.has(form.category) ? 'Method / Context' : 'Sub-type';

  return (
    <form onSubmit={handleSubmit} noValidate>
      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5',
          borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#991b1b', fontSize: 14,
        }}>
          {error}
        </div>
      )}

      {/* ── Classification ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Category" required>
          <select
            value={form.category}
            onChange={set('category')}
            required
            style={inputStyle}
          >
            <option value="">Select category…</option>
            {Object.entries(CATEGORIES).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </Field>

        <Field label={subcatLabel}>
          <select
            value={form.subcategory}
            onChange={set('subcategory')}
            style={inputStyle}
            disabled={!subs.length}
          >
            <option value="">None / Other</option>
            {subs.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Species / Resource name">
        <input
          type="text"
          value={form.species}
          onChange={set('species')}
          placeholder="e.g. Blueberry, Walleye, White birch…"
          style={inputStyle}
        />
      </Field>

      {/* ── Date & Season ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Harvest date" required>
          <input
            type="date"
            value={form.harvestDate}
            onChange={set('harvestDate')}
            required
            style={inputStyle}
          />
        </Field>

        <Field label="Season">
          <select
            value={form.season}
            onChange={set('season')}
            style={inputStyle}
          >
            <option value="">Select season…</option>
            {SEASONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          {form.season && (
            <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
              Auto-set from date — change if needed
            </p>
          )}
        </Field>
      </div>

      {/* ── Quantity ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Quantity">
          <input
            type="number"
            min={0}
            value={form.quantity}
            onChange={set('quantity')}
            placeholder="e.g. 5"
            style={inputStyle}
          />
        </Field>
        <Field label="Unit">
          <input
            type="text"
            value={form.unit}
            onChange={set('unit')}
            placeholder="e.g. kg, lbs, count, buckets…"
            style={inputStyle}
          />
        </Field>
      </div>

      <Field label="Abundance">
        <select value={form.abundance} onChange={set('abundance')} style={inputStyle}>
          <option value="">Not specified</option>
          {ABUNDANCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </Field>

      {/* ── Location ── */}
      <div
        style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0',
          borderRadius: 8, padding: '14px 16px', marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#166534' }}>
            📍 Location (optional)
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleUseMyLocation}
              disabled={gpsLoading}
              style={{
                padding: '6px 14px', background: gpsLoading ? '#9ca3af' : '#0ea5e9', color: '#fff',
                border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                cursor: gpsLoading ? 'not-allowed' : 'pointer', lineHeight: 1.4,
              }}
            >
              {gpsLoading ? '⏳ Locating…' : '📍 Use My Location'}
            </button>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              style={{
                padding: '6px 14px', background: '#16a34a', color: '#fff',
                border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', lineHeight: 1.4,
              }}
            >
              🗺 Pick on Map
            </button>
          </div>
        </div>
        {gpsError && (
          <div style={{ fontSize: 12, color: '#b91c1c', marginBottom: 8 }}>{gpsError}</div>
        )}

        {/* Coordinate summary when a pin is set */}
        {form.latitude !== '' && form.longitude !== '' && (
          <div style={{
            background: '#dcfce7', border: '1px solid #86efac',
            borderRadius: 6, padding: '7px 12px', marginBottom: 12,
            fontSize: 13, fontWeight: 600, color: '#166534',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span>📍 {parseFloat(form.latitude).toFixed(5)},  {parseFloat(form.longitude).toFixed(5)}</span>
            <button
              type="button"
              onClick={() => setForm((p) => ({ ...p, latitude: '', longitude: '' }))}
              style={{
                background: 'none', border: 'none', color: '#166534',
                cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: '0 4px',
              }}
              title="Clear coordinates"
            >
              ✕
            </button>
          </div>
        )}

        {/* Manual lat / lng inputs — always visible as fallback */}
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Or enter manually:</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Latitude">
            <input
              type="number"
              step="any"
              value={form.latitude}
              onChange={set('latitude')}
              placeholder="e.g. 51.0234"
              style={inputStyle}
            />
          </Field>
          <Field label="Longitude">
            <input
              type="number"
              step="any"
              value={form.longitude}
              onChange={set('longitude')}
              placeholder="e.g. -98.4567"
              style={inputStyle}
            />
          </Field>
        </div>
        <Field label="Location description">
          <input
            type="text"
            value={form.locationDescription}
            onChange={set('locationDescription')}
            placeholder="e.g. North shore of Lake Winnipeg, near the creek mouth…"
            style={inputStyle}
          />
        </Field>
      </div>

      {/* ── Coordinate picker modal ── */}
      {pickerOpen && (
        <CoordPickerModal
          initLat={form.latitude}
          initLng={form.longitude}
          onConfirm={handlePickerConfirm}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {/* ── Observations ── */}
      <Field label="Observations & notes">
        <textarea
          value={form.observations}
          onChange={set('observations')}
          rows={3}
          placeholder="Describe what you observed — conditions, wildlife, environmental notes…"
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </Field>

      <Field label="Traditional knowledge & cultural notes">
        <textarea
          value={form.traditionalKnowledge}
          onChange={set('traditionalKnowledge')}
          rows={3}
          placeholder="Traditional uses, cultural significance, ancestral knowledge…"
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </Field>

      <div style={{ ...fieldStyle, display: 'flex', alignItems: 'center', gap: 10 }}>
        <input
          type="checkbox"
          id="isTraditionalUse"
          checked={form.isTraditionalUse}
          onChange={set('isTraditionalUse')}
          style={{ width: 16, height: 16 }}
        />
        <label htmlFor="isTraditionalUse" style={{ fontSize: 14, color: '#374151', cursor: 'pointer' }}>
          Mark as traditional use site / practice
        </label>
      </div>

      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '9px 22px',
            background: loading ? '#9ca3af' : '#16a34a',
            color: '#fff', border: 'none', borderRadius: 6,
            fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Saving…' : 'Submit Record'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '9px 22px',
              background: '#f3f4f6', color: '#374151',
              border: '1px solid #d1d5db', borderRadius: 6,
              fontSize: 14, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
